import { Radio } from 'antd';
import { Controller, useFormContext } from 'react-hook-form';
import { AppAdminFormLabel, AppCheckbox } from '@components/atoms';
import { TextFieldWithLabel } from '@components/molecules';
import { useAdminBankAccountType } from '@/hooks/admin';
import { useJapaneseNameKanaFields } from '@/hooks/useJapaneseNameKanaFields';
import { RadioGroupProps } from 'antd/lib';
import { ClinicCreateFormData } from '@/pages/admin/site-admin-clinic-info/clinicFormSchema';
import { useParams } from 'react-router-dom';

const ReceiptFeeTransferForm = () => {
  const { site } = useParams<{ site: string }>();
  const currentSite = site ?? 'medix';
  const { data: bankAccountTypeData } = useAdminBankAccountType(currentSite);

  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<ClinicCreateFormData>();

  const transferBankKana = watch('receiptFeeTransfer.transfer_bank_name_kana');
  const transferBranchKana = watch('receiptFeeTransfer.transfer_bank_branch_name_kana');
  const transferAccountKana = watch('receiptFeeTransfer.transfer_bank_account_name_kana');

  const transferBankNameKanaFields = useJapaneseNameKanaFields({
    nameKanaValue: String(transferBankKana ?? ''),
    setNameValue: v =>
      setValue('receiptFeeTransfer.transfer_bank_name', v, { shouldDirty: true, shouldValidate: true }),
    setKanaValue: v =>
      setValue('receiptFeeTransfer.transfer_bank_name_kana', v, {
        shouldDirty: true,
        shouldValidate: true,
      }),
    maxLength: 255,
    fullWidthAlphaNumOnBlur: false,
  });

  const transferBranchNameKanaFields = useJapaneseNameKanaFields({
    nameKanaValue: String(transferBranchKana ?? ''),
    setNameValue: v =>
      setValue('receiptFeeTransfer.transfer_bank_branch_name', v, {
        shouldDirty: true,
        shouldValidate: true,
      }),
    setKanaValue: v =>
      setValue('receiptFeeTransfer.transfer_bank_branch_name_kana', v, {
        shouldDirty: true,
        shouldValidate: true,
      }),
    maxLength: 255,
    fullWidthAlphaNumOnBlur: false,
  });

  const transferAccountNameKanaFields = useJapaneseNameKanaFields({
    nameKanaValue: String(transferAccountKana ?? ''),
    setNameValue: v =>
      setValue('receiptFeeTransfer.transfer_bank_account_name', v, {
        shouldDirty: true,
        shouldValidate: true,
      }),
    setKanaValue: v =>
      setValue('receiptFeeTransfer.transfer_bank_account_name_kana', v, {
        shouldDirty: true,
        shouldValidate: true,
      }),
    maxLength: 255,
    fullWidthAlphaNumOnBlur: false,
  });

  const bankAccountTypeOptions: RadioGroupProps['options'] = (bankAccountTypeData?.data ?? []).map(
    ({ id, name }) => ({
      value: id,
      label: name,
    })
  );

  return (
    <div>
      <h3 className="text-xl my-4 font-bold">レセコン利用料口座振替情報</h3>

      <div className="grid grid-cols-3 gap-5">
        <Controller
          name="receiptFeeTransfer.transfer_bank_code"
          control={control}
          render={({ field }) => (
            <TextFieldWithLabel
              label="金融機関コード"
              {...field}
              value={field.value ?? ''}
              error={errors.receiptFeeTransfer?.transfer_bank_code}
            />
          )}
        />
        <Controller
          name="receiptFeeTransfer.transfer_bank_name"
          control={control}
          render={({ field }) => (
            <TextFieldWithLabel
              label="銀行名"
              {...field}
              value={field.value ?? ''}
              {...transferBankNameKanaFields.nameInputProps}
              error={errors.receiptFeeTransfer?.transfer_bank_name}
            />
          )}
        />
        <Controller
          name="receiptFeeTransfer.transfer_bank_name_kana"
          control={control}
          render={({ field }) => (
            <TextFieldWithLabel
              label="銀行名カナ"
              {...field}
              value={field.value ?? ''}
              {...transferBankNameKanaFields.kanaInputProps}
              error={errors.receiptFeeTransfer?.transfer_bank_name_kana}
            />
          )}
        />

        <Controller
          name="receiptFeeTransfer.transfer_bank_branch_code"
          control={control}
          render={({ field }) => (
            <TextFieldWithLabel
              label="支店番号"
              {...field}
              value={field.value ?? ''}
              error={errors.receiptFeeTransfer?.transfer_bank_branch_code}
            />
          )}
        />
        <Controller
          name="receiptFeeTransfer.transfer_bank_branch_name"
          control={control}
          render={({ field }) => (
            <TextFieldWithLabel
              label="支店名"
              {...field}
              value={field.value ?? ''}
              {...transferBranchNameKanaFields.nameInputProps}
              error={errors.receiptFeeTransfer?.transfer_bank_branch_name}
            />
          )}
        />
        <Controller
          name="receiptFeeTransfer.transfer_bank_branch_name_kana"
          control={control}
          render={({ field }) => (
            <TextFieldWithLabel
              label="支店名カナ"
              {...field}
              value={field.value ?? ''}
              {...transferBranchNameKanaFields.kanaInputProps}
              error={errors.receiptFeeTransfer?.transfer_bank_branch_name_kana}
            />
          )}
        />
      </div>

      <div className="grid grid-cols-4 gap-5 mt-4">
        <div>
          <AppAdminFormLabel label="口座種類" />
          <Controller
            name="receiptFeeTransfer.transfer_bank_account_type_id"
            control={control}
            render={({ field }) => (
              <Radio.Group
                {...field}
                options={bankAccountTypeOptions}
                onChange={e => {
                  field.onChange(e);
                  setValue('receiptFeeTransfer.transfer_bank_account_type_id', e.target.value, {
                    shouldValidate: true,
                  });
                }}
              />
            )}
          />
        </div>
        <Controller
          name="receiptFeeTransfer.transfer_bank_account_number"
          control={control}
          render={({ field }) => (
            <TextFieldWithLabel
              label="口座番号"
              {...field}
              value={field.value ?? ''}
              error={errors.receiptFeeTransfer?.transfer_bank_account_number}
            />
          )}
        />
        <Controller
          name="receiptFeeTransfer.transfer_bank_account_name"
          control={control}
          render={({ field }) => (
            <TextFieldWithLabel
              label="口座名義"
              {...field}
              value={field.value ?? ''}
              {...transferAccountNameKanaFields.nameInputProps}
              error={errors.receiptFeeTransfer?.transfer_bank_account_name}
            />
          )}
        />
        <Controller
          name="receiptFeeTransfer.transfer_bank_account_name_kana"
          control={control}
          render={({ field }) => (
            <TextFieldWithLabel
              label="口座名義カナ"
              {...field}
              value={field.value ?? ''}
              {...transferAccountNameKanaFields.kanaInputProps}
              error={errors.receiptFeeTransfer?.transfer_bank_account_name_kana}
            />
          )}
        />

        <Controller
          name="receiptFeeTransfer.billing_company_code"
          control={control}
          render={({ field }) => (
            <TextFieldWithLabel
              label="請求先企業番号"
              {...field}
              value={field.value ?? ''}
              error={errors.receiptFeeTransfer?.billing_company_code}
            />
          )}
        />
        <Controller
          name="receiptFeeTransfer.customer_code"
          control={control}
          render={({ field }) => (
            <TextFieldWithLabel
              label="顧客番号(自振)"
              {...field}
              value={field.value ?? ''}
              error={errors.receiptFeeTransfer?.customer_code}
            />
          )}
        />
        <div>
          <AppAdminFormLabel label="口座振替対象外" />
          <Controller
            name="receiptFeeTransfer.is_excluded_transfer"
            control={control}
            render={({ field }) => (
              <AppCheckbox
                checked={field.value ?? false}
                onChange={e => {
                  field.onChange(e.target.checked);
                  setValue('receiptFeeTransfer.is_excluded_transfer', e.target.checked, {
                    shouldValidate: true,
                  });
                }}
              />
            )}
          />
        </div>
        <div>
          <AppAdminFormLabel label="請求書希望" />
          <Controller
            name="receiptFeeTransfer.is_required_billing"
            control={control}
            render={({ field }) => (
              <AppCheckbox
                checked={field.value ?? false}
                onChange={e => {
                  field.onChange(e.target.checked);
                  setValue('receiptFeeTransfer.is_required_billing', e.target.checked, {
                    shouldValidate: true,
                  });
                }}
              />
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default ReceiptFeeTransferForm;
